import { useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { FloatingCard, type CardAnchor } from './floating-card'
import { ParticipantTile } from './participant-tile'
import { ShareViewer } from './share-viewer'
import { participantInitials, type TileParticipant } from '@/lib/meetings/rtk'

/**
 * The video dock (PLAN-085 §1.5, board m5gc): one draggable black card with
 * everyone's tile — your own included, mirrored — collapsible to a row of
 * avatars. The **document** is the page; video is deliberately the small
 * thing you can push out of the way, and where you put it is remembered for
 * the session. A remote screen share opens maximised for every viewer the
 * moment it starts (board m5gc #22) — once per share, so a viewer who
 * minimised it is not fought — and can be re-opened from its dock tile. The
 * dock itself sits above the overlay (z-40 vs z-30) so faces stay visible.
 */
export function VideoDock({
  self,
  remotes,
  activeSpeakerId,
  raisedHands,
  avatars,
  selfSpeaking,
  anchor,
}: {
  self: TileParticipant | null
  remotes: TileParticipant[]
  activeSpeakerId: string | null
  /** HUB user ids whose hand is up (board m5gc #12). */
  raisedHands: ReadonlySet<string>
  /** HUB user id → avatar URL, from the room's participant list (board m5gc #13). */
  avatars: ReadonlyMap<string, string | null>
  /** Your own microphone is picking up speech (board m5gc #21). */
  selfSpeaking: boolean
  /** Where the card starts before anyone drags it. */
  anchor: CardAnchor
}) {
  const { t } = useTranslation('meet')
  const [collapsed, setCollapsed] = useState(false)
  const [expandedShare, setExpandedShare] = useState<string | null>(null)
  /** The share (participant + track) that was last auto-maximised. */
  const autoOpened = useRef<string | null>(null)

  const everyone = [...(self ? [self] : []), ...remotes]
  const sharer = remotes.find((p) => p.screenShareEnabled) ?? null
  const shareKey = sharer ? `${sharer.id}:${sharer.screenShareTrack?.id ?? ''}` : null

  // Auto-maximise a new share exactly once; a viewer who minimised it keeps
  // it minimised until the sharer starts a new share.
  useEffect(() => {
    if (!sharer || !shareKey) {
      setExpandedShare(null)
      return
    }
    if (autoOpened.current === shareKey) return
    autoOpened.current = shareKey
    setExpandedShare(sharer.id)
  }, [sharer, shareKey])

  if (everyone.length === 0) return null

  return (
    <>
      {expandedShare && sharer && (
        <ShareViewer sharer={sharer} onMinimize={() => setExpandedShare(null)} />
      )}

      <FloatingCard
        storageKey="hub:meet:dock"
        anchor={anchor}
        handleLabel={t('dock.title', { count: everyone.length })}
        className="max-w-[calc(100%-1rem)]"
        handleExtra={
          <button
            type="button"
            aria-label={collapsed ? t('dock.expand') : t('dock.collapse')}
            onClick={() => setCollapsed((value) => !value)}
            onPointerDown={(event) => event.stopPropagation()}
            className="-mr-1 flex h-5 w-5 items-center justify-center rounded text-zinc-400 hover:bg-zinc-700 hover:text-white"
          >
            {collapsed ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
        }
      >
        {collapsed ? (
          // The minimised card: everyone's avatar (initials fallback), with the
          // active speaker ringed, so it still says who is here (board m5gc #18).
          <div className="flex items-center gap-1 p-1.5" data-testid="dock-collapsed">
            {everyone.slice(0, 6).map((participant) => {
              const avatarUrl = participant.userId
                ? (avatars.get(participant.userId) ?? null)
                : null
              return (
                <span
                  key={participant.id}
                  title={participant.isSelf ? t('dock.you') : participant.name}
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-zinc-700 text-[10px] font-semibold text-white',
                    (participant.id === activeSpeakerId || (participant.isSelf && selfSpeaking)) &&
                      'ring-2 ring-emerald-400'
                  )}
                >
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    participantInitials(participant)
                  )}
                </span>
              )
            })}
            {everyone.length > 6 && (
              <span className="px-1 text-xs text-zinc-400">+{everyone.length - 6}</span>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-stretch gap-1.5 p-1.5">
            {/* While the share is maximised it is already on screen — the
                dock keeps only the faces, which matters on a phone. */}
            {sharer && !expandedShare && (
              <button
                type="button"
                aria-label={t('dock.expandShare')}
                onClick={() => setExpandedShare(sharer.id)}
                className="block"
              >
                <ParticipantTile
                  participant={sharer}
                  showScreenShare
                  className="h-32 w-full sm:h-36"
                />
              </button>
            )}
            <div className="grid grid-cols-2 gap-1.5">
              {everyone.map((participant) => (
                <ParticipantTile
                  key={participant.id}
                  participant={participant}
                  speaking={participant.isSelf ? selfSpeaking : participant.id === activeSpeakerId}
                  handRaised={participant.userId !== null && raisedHands.has(participant.userId)}
                  avatarUrl={participant.userId ? (avatars.get(participant.userId) ?? null) : null}
                  className="h-20 w-28 sm:h-24 sm:w-32"
                />
              ))}
            </div>
          </div>
        )}
      </FloatingCard>
    </>
  )
}
